import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type IReimbursementAccountAddress = {
  address?: string | null
  address2?: string | null
  city?: string | null
  state?: number | null
  zipcode?: string | null
}

export type IReimbursementAccountDetail = {
  holders_name?: string | null
  birth_date?: string | null
  account_type?: number | null
}

export interface IReimbursementAccount {
  id: string
  address?: IReimbursementAccountAddress
  accountNumber?: string | null
  routingNumber?: string | null
  bankName?: string | null
  accountDetail?: IReimbursementAccountDetail
  nickName?: string | null
  submitted?: boolean
}

interface ReimbursementAccountsState {
  accounts: IReimbursementAccount[]
  currentAccount: IReimbursementAccount | null
  isEditing: boolean
}

const emptyAccount: IReimbursementAccount = {
  id: '',
  address: undefined,
  accountNumber: undefined,
  routingNumber: undefined,
  bankName: undefined,
  accountDetail: undefined,
  nickName: undefined,
  submitted: false
}

const initialState: ReimbursementAccountsState = {
  accounts: [],
  currentAccount: null,
  isEditing: false
}

const reimbursementAccountsSlice = createSlice({
  name: 'reimbursementAccounts',
  initialState,
  reducers: {
    addReimbursementAccount: (state, action: PayloadAction<IReimbursementAccount>) => {
      state.accounts.push(action.payload)
    },
    
    updateReimbursementAccount: (state, action: PayloadAction<IReimbursementAccount>) => {
      const index = state.accounts.findIndex(account => account.id === action.payload.id)
      if (index !== -1) {
        state.accounts[index] = action.payload
      }
    },
    
    deleteReimbursementAccount: (state, action: PayloadAction<string>) => {
      state.accounts = state.accounts.filter(account => account.id !== action.payload)
    },
    
    setCurrentAccount: (state, action: PayloadAction<string | null>) => {
      if (action.payload === null) {
        const newId = Date.now().toString()
        state.currentAccount = { ...emptyAccount, id: newId }
        state.isEditing = false
      } else {
        const account = state.accounts.find(acc => acc.id === action.payload)
        state.currentAccount = account || null
        state.isEditing = !!account
      }
    },
    
    createNewAccount: (state) => {
      const newId = Date.now().toString()
      state.currentAccount = { ...emptyAccount, id: newId }
      state.isEditing = false
    },
    
    setReimbursementAccountAddress: (state, action: PayloadAction<IReimbursementAccountAddress>) => {
      if (state.currentAccount) {
        state.currentAccount.address = action.payload
      }
    },
    
    setReimbursementAccountNumber: (state, action: PayloadAction<string>) => {
      if (state.currentAccount) {
        state.currentAccount.accountNumber = action.payload
      }
    },
    
    setReimbursementAccountRoutingNumber: (state, action: PayloadAction<string>) => {
      if (state.currentAccount) {
        state.currentAccount.routingNumber = action.payload
      }
    },
    
    setReimbursementAccountBankName: (state, action: PayloadAction<string>) => {
      if (state.currentAccount) {
        state.currentAccount.bankName = action.payload
      }
    },
    
    setReimbursementAccountDetail: (state, action: PayloadAction<IReimbursementAccountDetail>) => {
      if (state.currentAccount) {
        state.currentAccount.accountDetail = action.payload
      }
    },
    
    setReimbursementAccountNickname: (state, action: PayloadAction<string>) => {
      if (state.currentAccount) {
        state.currentAccount.nickName = action.payload
      }
    },
    
    setReimbursementAccountSubmitted: (state, action: PayloadAction<boolean>) => {
      if (state.currentAccount) {
        state.currentAccount.submitted = action.payload
      }
    },
    
    saveCurrentAccount: (state) => {
      if (state.currentAccount) {
        if (!Array.isArray(state.accounts)) {
          state.accounts = []
        }    
        const index = state.accounts.findIndex(account => account.id === state.currentAccount?.id)
        if (index !== -1) {
          state.accounts[index] = {...state.currentAccount}
        } else {
          state.accounts.push({...state.currentAccount})
        }
      }
    },
    
    resetReimbursementAccountsData: () => initialState, 
  },
})

export const {
  addReimbursementAccount,
  updateReimbursementAccount,
  deleteReimbursementAccount,
  setCurrentAccount,
  createNewAccount,
  setReimbursementAccountAddress,
  setReimbursementAccountNumber,
  setReimbursementAccountRoutingNumber,
  setReimbursementAccountBankName,
  setReimbursementAccountDetail,
  setReimbursementAccountNickname,
  setReimbursementAccountSubmitted,
  saveCurrentAccount,
  resetReimbursementAccountsData
} = reimbursementAccountsSlice.actions

export default reimbursementAccountsSlice.reducer